import { useState, useEffect, useMemo, useCallback } from 'react';
import translator from '@Components/Utilities/Translator';
import ConditionalWrapper from '@Components/Utilities/ConditionalWrapper';
import { watchlistOnClick } from '@Components/Utilities/SandhillsWatchlist';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { maxImageCount, gtmEventType, baseCatBlur } from '@Components/Utilities/SandhillsConstants';
import { useRecoilValue } from 'recoil';
import { gtmAlertPushEvent, sandhillsRequest, gtmPushEvent } from '@Components/Utilities/SandhillsUtility';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Slider from 'react-slick';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import CollectionLotBanner from '@Components/Shared/CollectionLotBanner';

const ListingGridImage = (props) => {

    const [imageLoaded, setImageLoaded] = useState(props.ForceLoadImages || !props.LazyLoad);
    const [updateStatus, setUpdateStatus] = useState(true);
    const [dataAction, setDataAction] = useState('add');
    const [isSavedToWatchlist, setIsSavedToWatchlist] = useState(props.IsWatchlist && !props.IsRecommendedListingListSearch ? props.IsWatchlist : false);
    const routes = useRecoilValue(atoms.routes);
    const currentUser = useRecoilValue(atoms.currentUser);
    const [activeIndex, setActiveIndex] = useState(0);
    const [hasSentImpression, setHasSentImpression] = useState(false);
    const windowSize = useWindowSize();
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const [loadingImageIndex, setLoadingImageIndex] = useState(null);
    const [backgroundLoadingIndex, setBackgroundLoadingIndex] = useState(null);

    const useBlur = baseCatBlur.includes(props.BaseCategoryId) || baseCatBlur.includes(props.CategoryID) || (windowSize.width < 1251 && windowSize.width > 550);

    const imageList = useMemo(() => {
        if (!windowSize.isMobile && props.ImageUrl && !useBlur) {
            return props.ImageUrl.map((image) => {
                if (image) {
                    return image.replace('w=350', 'w=250').replace('sz=Max', 'sz=Cover');
                }
                return image;
            });
        }
        if (windowSize.isMobile && props.ImageUrl && !useBlur) {
            return props.ImageUrl.map((image) => {
                if (image) {
                    return image.replace('sz=Max', 'sz=Cover');
                }
                return image;
            });
        }
        return props.ImageUrl;
    }, [props.ImageUrl, windowSize.isMobile]);

    const setWatchListAction = (isSaved) => {
        setIsSavedToWatchlist(isSaved);
        if (props.setSavedToWatchlist) {
            props.setSavedToWatchlist(isSaved);
        }
        setUpdateStatus(false);
        setDataAction(isSaved ? 'delete' : 'add');
    };

    useEffect(() => {
        if (props.unsaveWatchlist) {
            setIsSavedToWatchlist(false);
            if (props.setSavedToWatchlist) {
                props.setSavedToWatchlist(false);
            }
            setWatchListAction(false);
        } else if (props.UserWatchlist && props.UserWatchlist.length > 0) {
            if ((props.UserWatchlist.filter(c => c.ListingId == props.ListingId || c.Id == props.ListingId).length > 0) && updateStatus) {
                setWatchListAction(true);
            }
        }
    }, [props.UserWatchlist, props.unsaveWatchlist]);

    const dispatchImageLoaded = useCallback(() => {
        if (props.DispatchListings) {
            props.DispatchListings({
                type: 'update-listing',
                payload: {
                    Id: props.ListingId,
                    ImagesLoaded: true,
                }
            });
        }
    }, [props.DispatchListings, props.ListingId]);

    useEffect(function imageEffect() {
        if (imageLoaded) {
            dispatchImageLoaded();
        }
    }, [imageLoaded, dispatchImageLoaded]);

    const watchlistClick = (e) => {
        if (props.IsMobileCard) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (props.isWatchlistExpansionOpen) {
            const checkedCount = props.getExpansionCheckedCount?.();
            const noSelections = checkedCount == 0;

            if (noSelections) {
                setWatchListAction(false);
            }

            props.toggleWatchlistExpansion(checkedCount);
            return;
        }

        if (!isSavedToWatchlist) {
            watchlistOnClick(dataAction, props.ListingId, props.ListingTypes, routes);
            setWatchListAction(true);
            props.onWatchlistAdd?.();
        }

        props.toggleWatchlistExpansion();
    };

    const getWatchlistButton = () => {
        return (
            <span role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); watchlistClick(); } }} aria-label={isSavedToWatchlist ? translator.translate('sandhills.removefromwatchlistwithspace') : translator.translate('sandhills.addtowatchlistwithspace')} className={'image-watchlist ' + (isSavedToWatchlist ? 'watchlist-delet' : 'watchlist-ad')} data-listing-id={props.ListingId} data-action={(isSavedToWatchlist ? 'delete' : 'add')} onClick={(e) => { watchlistClick(e) }}>
                <i className='fas fa-heart'></i>
            </span>
        );
    };

    const changeImage = (image) => {
        var imageObj = new Image();
        imageObj.src = imageList[image];
        if (!imageObj.complete) {
            setImageLoaded(false);
        };
        gtmAlertPushEvent(gtmEventType.imageChange);
        if (!hasSentImpression && image == 1 && props.BaseCategoryId && props.ListingId) {
            // only send the impression once
            setHasSentImpression(true);
            sandhillsRequest({
                url: routes.ImageImpression,
                method: 'post',
                data: {
                    ListingID: props.ListingId,
                    baseCatID: props.BaseCategoryId,
                    HSPub: currentUser.HSTrackingUserID
                }
            })
        }
        setTimeout(() => {
            setActiveIndex(image);
        }, 525);
    };

    const getLeftFlavor = () => {
        let text = '';
        let name = '';
        let img = '';

        if (props.IsAT && props.ShowAuctionBranding) {
            if (props.HeaderText.includes(translator.translate('sandhills.featured'))) {
                text = translator.translate('sandhills.featured') + ' ';
            }
            text += translator.translate('sandhills.tradepubwebsite.mvc.onlineauction');
            name = 'at';
            img = <img src={routes.CDNBaseUrl + 'Images/Icons/Miscellaneous/at-icon-white.png'} alt="Auction Time Logo" />;
        } else if (props.ShowAuctionBranding && props.IsEF) {
            if (props.HeaderText) {
                text = props.HeaderText;
            }
            else {
                text += translator.translate('sandhills.tradepubwebsite.mvc.onsiteauction');
            }

            if (currentSite?.Site?.IsLivestockMarket || currentSite?.Site?.IsEquineMarket) {
                name = 'siteColorLSM';
            } else {
                name = 'ef';
            }
            img = <img src={routes.CDNBaseUrl + 'Images/Icons/Miscellaneous/ef-icon-black.png'} alt="Equipment Facts Logo" />;
        } else if (props.IsPreAuction) {
            text = translator.translate('sandhills.preauctiontime');
            name = 'pre-at';
        } else if (props.IsFeaturedListing && (!props.IsPreATResult && !currentSite?.Site?.IsHostedSite)) {
            text = translator.translate('sandhills.tradepubwebsite.mvc.featuredlisting');
            name = 'feat';
        } else if (props.IsPremiumListing) {
            text = translator.translate('sandhills.premiumlisting');
            name = 'prem';
            img = <i className='fas fa-star' />
        } else if (props.IsPreTrade) {
            text = translator.translate('sandhills.tradesites.mvc.pretradeoffer');
            name = 'pre-trade';
        } else if (props.IsPreATResult) {
            if (currentSite?.Site?.IsHostedSite) {
                text = "Pre-AuctionTime Result";
                name = 'pre-atresult';
            }
            else {
                text = translator.translate('sandhills.preauctiontime');
                name = 'pre-at';
            }
        } else if (props.ShowMediaOverlayBanner) {
            text = props.MediaOverlayBanner
            name = 'equipment-status';
        }
        if (text && name && name != 'equipment-status' && props.ShowMediaOverlayBanner) {
            return (
                <>
                    <span className={'left-flavor ' + name}>{text}</span>
                    <span className='left-flavor-bottom'>{props.MediaOverlayBanner}</span>
                </>
            );
        }
        else if (text && name) {
            return (
                <>
                    <span className={'left-flavor ' + name}>{props.IsMobileCard && img && <>{img}&nbsp;</>}{text}</span>
                </>
            );
        }
        return props.IsMobileCard ? <span /> : '';
    }

    const NextArrow = (props) => {
        const { onClick } = props;
        const lastIndex = imageList?.length - 1;
        return (
            <i style={{ display: activeIndex == lastIndex ? 'none' : '' }} className='fas fa-chevron-right arrow arrow-right' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); onClick(); } }} aria-label={translator.translate('sandhills.nextimage')} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}></i>
        );
    }

    const PrevArrow = (props) => {
        const { onClick } = props;
        return (
            <i style={{ display: activeIndex == 0 ? 'none' : '' }} className='fas fa-chevron-left arrow arrow-left' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); onClick(); } }} aria-label={translator.translate('sandhills.previmage')} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}></i>
        );
    }

    const settings = useMemo(() => ({
        dots: true,
        infinite: false,
        speed: 525,
        cssEase: "ease",
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: 0,
        arrows: (windowSize.isMobile || windowSize.isTablet) ? false : true,
        swipe: (windowSize.isMobile || windowSize.isTablet) ? true : false,
        draggable: false,
        fade: false,
        touchThreshold: 10,
        appendDots: dots => (
            <div
                style={{
                    backgroundColor: 'rgba(0, 0, 0, .6)',
                    fontSize: '8px',
                    color: '#fff',
                    display: 'flex',
                    gap: '4px',
                    position: 'absolute',
                    bottom: '8px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    left: '0',
                    right: '0',
                    width: 'fit-content',
                    margin: 'auto',
                    padding: '3px 4px',
                    borderRadius: '50px'
                }}
            >
                <ul style={{ margin: '0px' }}>{dots}</ul>
            </div>
        ),
        customPaging: i => (
            <div>
                <i tabIndex='0' className={activeIndex == i ? 'fas fa-circle' : "far fa-circle"}></i>
            </div>
        ),
        beforeChange: (current, next) => {
            if (current != next) {
                changeImage(next);
            }
        },
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        useCss: false,
        useTransform: false
    }), [windowSize.isMobile, activeIndex, changeImage]);

    const blurBackgroundStyle = {
        position: 'absolute',
        height: props.IsParts ? '135px' : props.IsMobileCard ? '140px' : '220px',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        filter: 'blur(5px)',
        width: '100%',
        objectFit: 'cover',
        opacity: '.25'
    };

    const blurImageStyle = {
        position: !props.IsParts && 'absolute',
        height: props.IsParts ? '135px' : 'unset',
    };

    const blurMaxImageStyle = {
        position: 'absolute',
        height: props.IsParts ? '135px' : props.IsMobileCard ? '140px' : '220px',
        opacity: '0.4'
    };

    const maxImageStyle = {
        height: props.IsParts ? '135px' : '200px',
        opacity: '0.4',
        width: '100%',
        objectFit: props.IsParts && 'fill'
    };
    const imageStyle = {
        height: props.IsParts ? '135px' : props.NoImage ? '130px' : props.IsMobileCard ? '140px' : '200px',
        width: props.IsParts && imageList.length > 1 ? '' : '100%',
        objectFit: props.IsParts ? 'fill' : props.NoImage && 'unset'
    };

    return (
        <>
            {props.IsMobileCard &&
                <span className='top-banner mobile-card'>
                    {getLeftFlavor()}
                </span>
            }
            <div className='listing-image' style={{ 'width': imageList.length > 1 ? '100%' : '' }} >
                <div className='listing-image-container' style={{ 'height': useBlur && !props.IsParts && !props.IsMobileCard ? '220px' : props.IsParts ? '135px' : props.IsMobileCard && !props.useBlur ? '140px' : props.useBlur ? '160px' : '200px', maxHeight: useBlur && !props.IsParts ? '220px' : props.IsParts ? '135px' : props.IsMobileCard ? '140px' : '200px' }}>
                    {!props.IsMobileCard && <span className='top-banner'>
                        {getLeftFlavor()}
                        <span className='right-flavor'>
                            {props.ShowWatchlist && getWatchlistButton()}
                        </span>
                    </span>}
                    <ConditionalWrapper condition={props.DetailUrl} wrapper={children =>
                        <a href={props.DetailUrl} className={props.NoImage ? "no-image" : ''} onClick={() => props.IsSimilar && gtmPushEvent(gtmEventType.noListingsSimilarResultClick)} target={props.LinkTarget} aria-label={translator.translate('sandhills.tradepubwebsite.mvc.viewdetailsfor') + " " + props.AriaLabelTitle + (props.LinkTarget == "_blank" ? ' (' + translator.translate("sandhills.opensinanewtab") + ')' : '')}>
                            {props.IsMobileCard &&
                                <span className='right-flavor'>
                                    {props.ShowWatchlist && getWatchlistButton()}
                                </span>}
                            {children}
                        </a>}>
                        {imageList.length > 1 ?
                            <Slider {...settings} style={{ 'height': useBlur && !props.IsParts && !props.IsMobileCard ? '220px' : props.IsParts ? '135px' : props.IsMobileCard && !props.useBlur ? '140px' : props.useBlur ? '160px' : '200px', maxHeight: useBlur && !props.IsParts ? '220px' : props.IsParts ? '135px' : '200px', width: '100%' }}>
                                {imageList.map((image, index) => {
                                    return (
                                        <div key={props.ListingId + ' - ' + index}>
                                            <div style={{ position: 'relative' }} className={props.IsMobileCard && useBlur ? 'blur-image' : ''}>
                                                {(loadingImageIndex === index || backgroundLoadingIndex === index) && index > 0 && (
                                                    <LoadingRing sxBox={{ position: 'absolute', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', zIndex: 9999, }} />
                                                )}
                                                {useBlur ?
                                                    <div style={{ position: 'relative', height: props.IsParts ? '135px' : props.IsMobileCard ? '160px' : '220px' }}>
                                                        {index == maxImageCount ?
                                                            <>
                                                                <span className='contain'><div tabIndex={activeIndex != maxImageCount ? '-1' : 0} target={props.LinkTarget} aria-label={props.AltText} className='view-all' onClick={(e) => { gtmAlertPushEvent(gtmEventType.viewAllImagesClick) }}>{translator.translate('sandhills.seeall')} {props.ImageCount} {translator.translate('sandhills.photos')}</div></span>
                                                                <LazyLoadImage style={blurMaxImageStyle} loading={!props.LazyLoad && index == 0 ? 'eager' : 'lazy'} fetchpriority={!props.LazyLoad && index == 0 ? 'high' : 'low'} className={'listing-main-image'} src={image} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId + ' - ' + index} onLoad={() => setLoadingImageIndex(null)} onError={() => setLoadingImageIndex(null)} beforeLoad={() => setLoadingImageIndex(index)} />
                                                            </> :
                                                            <>                                   
                                                                {props.ForceLoadImages && index == 0 ?
                                                                    <>
                                                                        <img style={blurBackgroundStyle} loading='eager' fetchpriority='high' src={image} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId + ' - ' + index} onLoad={() => setBackgroundLoadingIndex(null)} onError={() => setBackgroundLoadingIndex(null)} onLoadStart={() => setBackgroundLoadingIndex(index)} />
                                                                        <img fetchpriority='high' loading='eager' style={blurImageStyle} className='listing-main-image' src={imageList[activeIndex]} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId} onLoad={() => setLoadingImageIndex(null)} onError={() => setLoadingImageIndex(null)} onLoadStart={() => setLoadingImageIndex(index)} />
                                                                    </> :
                                                                    <>   
                                                                        <LazyLoadImage style={blurBackgroundStyle} loading={!props.LazyLoad && index == 0 ? 'eager' : 'lazy'} fetchpriority={!props.LazyLoad && index == 0 ? 'high' : 'low'} src={image} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId + ' - ' + index} onLoad={() => setBackgroundLoadingIndex(null)} onError={() => setBackgroundLoadingIndex(null)} beforeLoad={() => setBackgroundLoadingIndex(index)} />
                                                                        <LazyLoadImage style={blurImageStyle} loading={!props.LazyLoad && index == 0 ? '' : 'lazy'} fetchpriority={!props.LazyLoad && index == 0 ? 'high' : 'low'} className='listing-main-image' src={image} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId + ' - ' + index} onLoad={() => setLoadingImageIndex(null)} onError={() => setLoadingImageIndex(null)} beforeLoad={() => setLoadingImageIndex(index)} />
                                                                    </>
                                                                }
                                                            </>

                                                        }
                                                    </div> :
                                                    (index == maxImageCount ?
                                                        <>
                                                            <span className='contain' style={{ maxWidth: '100%' }}><div tabIndex={activeIndex != maxImageCount ? '-1' : 0} target={props.LinkTarget} aria-label={props.AltText} className='view-all' onClick={(e) => { gtmAlertPushEvent(gtmEventType.viewAllImagesClick) }}>{translator.translate('sandhills.seeall')} {props.ImageCount} {translator.translate('sandhills.photos')}</div></span>
                                                            <LazyLoadImage style={maxImageStyle} loading={!props.LazyLoad && index == 0 ? '' : 'lazy'} fetchpriority={!props.LazyLoad && index == 0 ? 'high' : 'low'} className={'listing-main-img'} src={image} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId + ' - ' + index} onLoad={() => setLoadingImageIndex(null)} onError={() => setLoadingImageIndex(null)} beforeLoad={() => setLoadingImageIndex(index)} />
                                                        </> :
                                                        <>
                                                            {props.ForceLoadImages && index == 0 ?
                                                                <img style={imageStyle} fetchpriority='high' loading='eager' className='listing-main-img' src={image} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId + ' - ' + index} onLoad={() => setLoadingImageIndex(null)} onError={() => setLoadingImageIndex(null)} onLoadStart={() => setLoadingImageIndex(index)} />
                                                                :
                                                                <LazyLoadImage style={imageStyle} loading={!props.LazyLoad && index == 0 ? '' : 'lazy'} fetchpriority={!props.LazyLoad && index == 0 ? 'high' : 'low'} className='listing-main-img' src={image} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId + ' - ' + index} onLoad={() => setLoadingImageIndex(null)} onError={() => setLoadingImageIndex(null)} beforeLoad={() => setLoadingImageIndex(index)} />
                                                            }
                                                        </>
                                                    )}
                                            </div>
                                        </div>)
                                })}
                            </Slider> :
                            (useBlur && !props.NoImage ?
                                <>
                                    {props.ForceLoadImages ?
                                        <>
                                            <img style={blurBackgroundStyle} loading='eager' fetchpriority='high' src={imageList[activeIndex]} alt={props.AltText ? props.AltText : props.ListingTitle + ' - background - ' + props.ListingId} />
                                            <img fetchpriority='high' loading='eager' style={blurImageStyle} className='listing-main-image' src={imageList[activeIndex]} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId} />
                                        </> :
                                        <>
                                            <LazyLoadImage style={blurBackgroundStyle} loading={props.LazyLoad ? 'lazy' : 'eager'} fetchpriority={props.LazyLoad ? 'low' : 'high'} src={imageList[activeIndex]} alt={props.AltText ? props.AltText : props.ListingTitle + ' - background - ' + props.ListingId} />
                                            <LazyLoadImage loading={props.LazyLoad ? 'lazy' : 'eager'} fetchpriority={props.LazyLoad ? 'low' : 'high'} style={blurImageStyle} className='listing-main-image' src={imageList[activeIndex]} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId} />
                                        </>
                                    }
                                </>
                                :
                                <>
                                    {
                                        props.ForceLoadImages ?
                                            <img style={imageStyle} fetchpriority='high' loading='eager' className='listing-main-img' src={imageList[activeIndex]} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId} onLoad={() => setLoadingImageIndex(null)} />
                                            :
                                            <LazyLoadImage style={imageStyle} loading={props.LazyLoad ? 'lazy' : 'eager'} fetchpriority={props.LazyLoad ? 'low' : 'high'} className={useBlur ? 'listing-main-image' : 'listing-main-img'} src={imageList[activeIndex]} alt={props.AltText ? props.AltText : props.ListingTitle + ' - ' + props.ListingId} />
                                    }
                                </>
                            )
                        }
                    </ConditionalWrapper>
                </div>
                {props.CollectionInfo?.Listings && <CollectionLotBanner collectionInfo={props.CollectionInfo} listingId={props.ListingId} />}
            </div>
        </>
    );
};

export default ListingGridImage;